import { useCallback, useEffect, useState } from 'react';
import {
  fetchWatcherPresence,
  fetchWatcherToken,
  queueWatcherJob,
  regenerateWatcherToken,
  WATCHER_DOWNLOAD_URL,
  type WatcherPresence,
  type WatcherToken,
} from '../api/watcher';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { errorText } from './useSessionLive';
import { fmtMoment } from './dates';

const when = (iso: string) => fmtMoment(iso);

/**
 * The watcher on /proves (docs/design/HUMAN_TESTING_STUDY.md §5, §6): the
 * download that already carries the tester's own token, whether the platform
 * publishes one at all, who can lend a machine right now, and, for an admin,
 * a way to open or close the game on a lendable machine for a session.
 */
export function WatcherCard({
  isAdmin,
  onError,
}: {
  isAdmin: boolean;
  onError: (e: string | null) => void;
}) {
  const t = ca.watcherCard;
  const [token, setToken] = useState<WatcherToken | null>(null);
  const [presence, setPresence] = useState<WatcherPresence[]>([]);
  const [session, setSession] = useState('');
  const [queued, setQueued] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchWatcherToken()
      .then(setToken)
      .catch(() => setToken(null));
    fetchWatcherPresence()
      .then(setPresence)
      .catch(() => setPresence([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = () =>
    regenerateWatcherToken()
      .then((next) => {
        setToken(next);
        onError(null);
      })
      .catch((e) => onError(errorText(e)));

  const job = (accountId: number, kind: 'connect' | 'disconnect') =>
    queueWatcherJob({ accountId, kind, sessionCode: session || undefined })
      .then((j) => {
        setQueued(t.queued(j.kind, j.id));
        onError(null);
      })
      .catch((e) => onError(errorText(e)));

  const lendable = presence.filter((p) => p.present && p.lendable);

  return (
    <section className="card" aria-label={t.title}>
      <h2>
        <i className="bi bi-pc-display" aria-hidden="true" /> {t.title}
      </h2>
      <p className="muted small">{t.intro}</p>
      {token && !token.published && <p className="muted small">{t.notPublished}</p>}
      {token && token.published && (
        <p className="row actions">
          <a className="button primary" href={WATCHER_DOWNLOAD_URL} download>
            <i className="bi bi-download" aria-hidden="true" /> {t.download(token.version ?? '')}
          </a>
          {token.hasToken && (
            <button type="button" onClick={regenerate}>
              {t.regenerate}
            </button>
          )}
          <span className="muted small">{token.hasToken ? t.tokenSet : t.tokenUnset}</span>
        </p>
      )}
      <h3>{t.lendable}</h3>
      {lendable.length === 0 && <p className="muted small">{t.nobody}</p>}
      <ul className="list">
        {lendable.map((p) => (
          <li key={p.accountId}>
            <strong>{p.handle}</strong>{' '}
            <span className="muted small">
              {p.gameRunning ? t.gameOpen : t.gameClosed}
              {p.version ? ` · v${p.version}` : ''}
              {p.hotkey ? ` · ${t.hotkey}` : ''}
              {t.awake(p.keepAwake)}
              {p.locked ? ` · ${t.locked}` : ''} · {t.dlcs(p.ownedDlcs)} · {when(p.lastSeenAt)}
            </span>
            {isAdmin && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => job(p.accountId, 'connect')}
                  disabled={!session}
                >
                  {t.connect}
                </button>{' '}
                <button type="button" onClick={() => job(p.accountId, 'disconnect')}>
                  {t.disconnect}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {isAdmin && (
        <p className="row">
          <label>
            {t.sessionForJob}
            <input
              value={session}
              onChange={(e) => setSession(e.target.value)}
              placeholder="wrt45"
            />
          </label>
          {queued && (
            <span className="muted small" role="status">
              {queued}
            </span>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * The watcher on a card's own page (the owner, 2026-09-29): a card's how-to
 * begins "open the watcher before the game", and the only download was on
 * the list, folded, a page away from the card a tester was sent to. Why this
 * test wants it, the download, the three moves that have it running, and
 * whether the platform sees the caller's own right now.
 */
export function WatcherDownload({ kind }: { kind: string }) {
  const t = ca.watcherCard;
  const { me } = useAuth();
  const [token, setToken] = useState<WatcherToken | null>(null);
  const [present, setPresent] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    fetchWatcherToken()
      .then((next) => {
        if (alive) setToken(next);
      })
      .catch(() => {
        if (alive) setToken(null);
      });
    fetchWatcherPresence()
      .then((list) => {
        if (alive) setPresent(list.some((p) => p.accountId === me.id && p.present));
      })
      .catch(() => {
        if (alive) setPresent(null);
      });
    return () => {
      alive = false;
    };
  }, [me.id]);

  return (
    <div className="watcher-download" role="group" aria-label={t.title}>
      <h3>
        <i className="bi bi-pc-display" aria-hidden="true" /> {t.title}
      </h3>
      <p>{t.forCard[kind] ?? t.intro}</p>
      {present !== null && (
        <p className="small">
          <span className={`chip small ${present ? 'ok' : ''}`}>
            {present ? t.present : t.absent}
          </span>
        </p>
      )}
      {token && !token.published && <p className="muted small">{t.notPublished}</p>}
      {token && token.published && (
        <>
          <p className="row actions">
            <a className="button primary" href={WATCHER_DOWNLOAD_URL} download>
              <i className="bi bi-download" aria-hidden="true" /> {t.download(token.version ?? '')}
            </a>
          </p>
          <ol className="how">
            {t.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
